import React, { useState, useEffect } from 'react';
import { progressApi, patientsApi, therapyApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX } from 'react-icons/hi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { usePermissions } from '../../hooks/usePermissions';
import { formatValidationErrors } from '../../utils/errorHandler';
import { formatDateForInputIST, todayYmdIST, addCalendarDaysYmd } from '../../utils/timezoneDisplay';

const ProgressReportForm = ({ report, patient, initialReportType = 'progress_report', onClose, onSuccess }) => {
  const { canCreate, canUpdate } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [therapyPlans, setTherapyPlans] = useState([]);
  const [formData, setFormData] = useState({
    patient_id: patient?.id || '',
    therapy_plan_id: '',
    report_type: initialReportType,
    report_period_start: '',
    report_period_end: '',
    activities_done: '',
    improvements: '',
    therapy_response: '',
    issues_encountered: '',
    next_steps: '',
    home_plan_content: '',
    doctor_notes: '',
    parent_feedback: '',
    status: 'draft'
  });

  const homePlanToolbarModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean']
    ]
  };

  useEffect(() => {
    fetchPatients();
    if (report) {
      // Load report data for editing
      setFormData({
        patient_id: report.patient_id || '',
        therapy_plan_id: report.therapy_plan_id || '',
        report_type: report.report_type || 'progress_report',
        report_period_start: formatDateForInputIST(report.report_period_start),
        report_period_end: formatDateForInputIST(report.report_period_end),
        activities_done: report.activities_done || '',
        improvements: report.improvements || '',
        therapy_response: report.therapy_response || '',
        issues_encountered: report.issues_encountered || '',
        next_steps: report.next_steps || '',
        home_plan_content: report.home_plan_content || '',
        doctor_notes: report.doctor_notes || '',
        parent_feedback: report.parent_feedback || '',
        status: report.status || 'draft'
      });
      if (report.patient_id) {
        fetchTherapyPlans(report.patient_id);
      }
    } else if (patient) {
      setFormData(prev => ({
        ...prev,
        patient_id: patient.id,
        report_type: initialReportType || 'progress_report'
      }));
      fetchTherapyPlans(patient.id);
      const endYmd = todayYmdIST();
      const startYmd = addCalendarDaysYmd(endYmd, -30);
      setFormData(prev => ({
        ...prev,
        patient_id: patient.id,
        report_type: initialReportType || 'progress_report',
        report_period_start: startYmd,
        report_period_end: endYmd,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        report_type: initialReportType || 'progress_report'
      }));
    }
  }, [report, patient, initialReportType]);

  const fetchPatients = async () => {
    try {
      const response = await patientsApi.getAll({ limit: 1000 });
      setPatients(response.data.data.patients || []);
    } catch (error) {
      toast.error('Failed to fetch patients');
    }
  };

  const fetchTherapyPlans = async (patientId) => {
    try {
      const response = await therapyApi.getPlans({ patient_id: patientId, status: 'active' });
      setTherapyPlans(response.data.data.plans || []);
    } catch (error) {
      // Handle error silently - patient might not have therapy plans
    }
  };

  const handlePatientChange = (patientId) => {
    setFormData(prev => ({
      ...prev,
      patient_id: patientId,
      therapy_plan_id: ''
    }));
    if (patientId) {
      fetchTherapyPlans(patientId);
    } else {
      setTherapyPlans([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleHomePlanEditorChange = (value) => {
    setFormData(prev => ({
      ...prev,
      home_plan_content: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions
    if (report && !canUpdate('progress')) {
      toast.error('You do not have permission to update progress reports');
      return;
    }
    if (!report && !canCreate('progress')) {
      toast.error('You do not have permission to create progress reports');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        patient_id: parseInt(formData.patient_id),
        therapy_plan_id: formData.therapy_plan_id ? parseInt(formData.therapy_plan_id) : null,
        report_type: formData.report_type || 'progress_report',
        report_period_start: formData.report_period_start || null,
        report_period_end: formData.report_period_end || null,
        activities_done: formData.activities_done || null,
        improvements: formData.improvements || null,
        therapy_response: formData.therapy_response || null,
        issues_encountered: formData.issues_encountered || null,
        next_steps: formData.next_steps || null,
        home_plan_content: formData.home_plan_content || null,
        doctor_notes: formData.doctor_notes || null,
        parent_feedback: formData.parent_feedback || null,
        status: formData.status
      };

      if (report) {
        await progressApi.update(report.id, submitData);
        toast.success('Progress report updated successfully');
      } else {
        await progressApi.create(submitData);
        toast.success('Progress report created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">
            {report ? 'Edit Progress Report' : 'Create Progress Report'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Type <span className="text-red-500">*</span>
            </label>
            <select
              name="report_type"
              value={formData.report_type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="progress_report">Progress Report</option>
              <option value="home_plan_report">Home Plan Report</option>
            </select>
          </div>

          {/* Patient and Period */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Patient <span className="text-red-500">*</span>
              </label>
              <select
                name="patient_id"
                value={formData.patient_id}
                onChange={(e) => handlePatientChange(e.target.value)}
                required
                disabled={!!patient}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select Patient</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name} ({p.patient_code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period Start
              </label>
              <input
                type="date"
                name="report_period_start"
                value={formData.report_period_start}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period End
              </label>
              <input
                type="date"
                name="report_period_end"
                value={formData.report_period_end}
                onChange={handleChange}
                min={formData.report_period_start || undefined}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Therapy Plan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Therapy Plan (Optional)
            </label>
            <select
              name="therapy_plan_id"
              value={formData.therapy_plan_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select Therapy Plan</option>
              {therapyPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.plan_type} - {plan.therapy_type || 'N/A'}
                </option>
              ))}
            </select>
          </div>

          {formData.report_type === 'home_plan_report' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Home Plan Report Content
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={formData.home_plan_content}
                  onChange={handleHomePlanEditorChange}
                  modules={homePlanToolbarModules}
                  className="home-plan-editor"
                  placeholder="Write detailed home plan report manually..."
                />
              </div>
            </div>
          ) : (
            <>
              {/* Activities Done */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activities Done
                </label>
                <textarea
                  name="activities_done"
                  value={formData.activities_done}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe the activities and exercises done during this period..."
                />
              </div>

              {/* Improvements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Improvements Observed
                </label>
                <textarea
                  name="improvements"
                  value={formData.improvements}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe the improvements and progress made..."
                />
              </div>

              {/* Therapy Response */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Therapy Response
                </label>
                <textarea
                  name="therapy_response"
                  value={formData.therapy_response}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="How the patient responded to therapy..."
                />
              </div>

              {/* Issues Encountered */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issues Encountered
                </label>
                <textarea
                  name="issues_encountered"
                  value={formData.issues_encountered}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Any challenges or issues faced during therapy..."
                />
              </div>

              {/* Next Steps */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Next Steps
                </label>
                <textarea
                  name="next_steps"
                  value={formData.next_steps}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Recommended next steps and goals..."
                />
              </div>
            </>
          )}

          {/* Status */}
          {report && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="draft">Draft</option>
                <option value="pending_review">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="sent">Sent</option>
              </select>
            </div>
          )}

          {/* Doctor Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Doctor Notes
            </label>
            <textarea
              name="doctor_notes"
              value={formData.doctor_notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional notes from the doctor..."
            />
          </div>

          {/* Parent Feedback */}
          {report && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Feedback
              </label>
              <textarea
                name="parent_feedback"
                value={formData.parent_feedback}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Feedback from parents..."
              />
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : report ? 'Update Report' : 'Create Report'}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .home-plan-editor .ql-container {
          min-height: 220px;
        }
      `}</style>
    </div>
  );
};

export default ProgressReportForm;

